import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { FoodItemInflowModel } from 'src/app/models/food-item-inflow.model';
import {
  AyBService,
  PaginatedWarehouseFoodInflowsModel,
} from 'src/app/services/api/ayb.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-inventario',
  templateUrl: './inventario.component.html',
  styleUrls: ['./inventario.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventarioComponent implements OnInit, OnDestroy {
  private _unsubscribeOnDestroy$: Subject<boolean> = new Subject<boolean>();
  isLoading = true;
  loadedItemsCount = 0;
  displayedColumns = [
    'fileNumber',
    'detalleInsumos',
    'proveedor',
    'observation',
    'factura',
    'ubicacion',
    'efectiveDate',
    'cantidad',
    'costo',
    'costoTotal',
    'editar',
  ];

  warehouseFoodInflows: FoodItemInflowModel[] = [];
  dataSource = new MatTableDataSource<any>();

  selectProveedor: string | null = '';
  selectFactura: string | null = null;
  selectUbicacion: string | null = null;

  startDate: Date | any;
  endDate: Date | any;

  suppliersList: string[] = [];
  invoiceNumbers: string[] = [];
  arrayUbicaciones = ['Cuzco', 'Huaraz', 'Lima'];

  paginator!: MatPaginator;
  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    if (this.paginator) {
      this.dataSource.paginator = paginator;
    }
  }
  sort!: MatSort;
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    this.sort = sort;
    if (this.sort) {
      this.dataSource.sort = sort;
    }
  }

  constructor(
    public dialog: MatDialog,
    private readonly router: Router,
    private readonly aybService: AyBService,
    private readonly cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadWarehouseFoodInflows();
  }

  async loadWarehouseFoodInflows(): Promise<void> {
    this.isLoading = true;
    this.loadedItemsCount = 0;
    this.cdRef.markForCheck();

    const allInflows: FoodItemInflowModel[] = [];
    let nextKeyId: string | null | undefined = null;

    try {
      do {
        const response: {
          getAllWarehouseFoodInflows: PaginatedWarehouseFoodInflowsModel;
        } = await firstValueFrom(
          this.aybService
            .queryGetAllWarehouseFoodInflows(nextKeyId)
            .pipe(takeUntil(this._unsubscribeOnDestroy$)),
        );

        const payload: PaginatedWarehouseFoodInflowsModel | undefined =
          response?.getAllWarehouseFoodInflows;
        const currentChunk: FoodItemInflowModel[] = payload?.inflows || [];

        if (currentChunk.length > 0) {
          allInflows.push(...currentChunk);
          this.loadedItemsCount = allInflows.length;
          this.cdRef.markForCheck();
        }

        nextKeyId = payload?.nextEvaluatedKeyId ?? null;
      } while (Boolean(nextKeyId));

      this.warehouseFoodInflows = allInflows
        .map((inflow) => {
          const activeDetalleInsumos =
            inflow.detalleInsumos?.filter((item) => !item.deletedAt) || [];
          return {
            ...inflow,
            detalleInsumos: activeDetalleInsumos,
            costoTotal:
              activeDetalleInsumos.reduce(
                (acc, item) => acc + (item?.costo || 0),
                0,
              ) || 0,
            efectiveDate:
              inflow?.fechaFactura ||
              inflow?.fechaRecibo ||
              inflow?.acquisitionDate ||
              inflow?.createdAt ||
              null,
          };
        })
        .sort((inflowA, inflowB) => {
          return (
            new Date(
              inflowB?.createdAt ||
                inflowB?.fechaFactura ||
                inflowB?.fechaRecibo ||
                inflowB?.acquisitionDate ||
                '-',
            ).getTime() -
            new Date(
              inflowA?.createdAt ||
                inflowA?.fechaFactura ||
                inflowA?.fechaRecibo ||
                inflowA?.acquisitionDate ||
                '-',
            ).getTime()
          );
        });

      const supplierNames = this.warehouseFoodInflows.map(
        (foodInflow) => foodInflow.proveedor?.razonSocial,
      );
      const invoiceNumbers = this.warehouseFoodInflows.map(
        (foodInflow) => foodInflow.numFactura,
      );

      this.suppliersList = [
        ...new Set(supplierNames.filter(Boolean)),
      ] as string[];

      this.invoiceNumbers = [
        ...new Set(invoiceNumbers.filter(Boolean)),
      ] as string[];

      this.dataSource.data = this.warehouseFoodInflows;
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    } catch (error) {
      console.error('Error fetching almacen insumos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los insumos del almacén. Intente nuevamente. Si el error persiste, contactar al administrador.',
      });
    } finally {
      this.isLoading = false;
      this.cdRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  applyFilterSelect() {
    let sortedFoodInflows = this.warehouseFoodInflows;

    if (this.selectProveedor) {
      sortedFoodInflows = sortedFoodInflows?.filter(
        (foodInflow) =>
          foodInflow.proveedor?.razonSocial === this.selectProveedor,
      );
    }
    if (this.selectFactura) {
      sortedFoodInflows = sortedFoodInflows?.filter(
        (foodInflow) => foodInflow.numFactura === this.selectFactura,
      );
    }
    if (this.selectUbicacion) {
      sortedFoodInflows = sortedFoodInflows?.filter(
        (foodInflow) => foodInflow.locacion === this.selectUbicacion,
      );
    }
    if (this.startDate && this.endDate) {
      console.log({
        startDate: this.startDate,
        endDate: this.endDate,
      });
      sortedFoodInflows = sortedFoodInflows?.filter((foodInflow) => {
        return (
          new Date(this.startDate) <=
            new Date(
              foodInflow?.fechaFactura ||
                foodInflow?.fechaRecibo ||
                foodInflow?.acquisitionDate ||
                foodInflow?.createdAt ||
                '',
            ) &&
          new Date(
            foodInflow?.fechaFactura ||
              foodInflow?.fechaRecibo ||
              foodInflow?.acquisitionDate ||
              foodInflow?.createdAt ||
              '-',
          ) <= new Date(this.endDate)
        );
      });
    }

    // Ensure filtered data also has the computed efectiveDate property
    const dataWithEfectiveDate = sortedFoodInflows.map((inflow) => ({
      ...inflow,
      efectiveDate:
        inflow?.fechaFactura ||
        inflow?.fechaRecibo ||
        inflow?.acquisitionDate ||
        inflow?.createdAt ||
        null,
    }));

    this.dataSource.data = dataWithEfectiveDate;
  }

  cleanDateSelected() {
    this.startDate = null;
    this.endDate = null;
    this.applyFilterSelect();
  }

  navigateToEditIngreso(foodInflow: any) {
    console.log({ foodInflow });
    this.router
      .navigate([`/gestion/ingresos/${foodInflow.id}`])
      .then(() => console.log('Navigated to Editar Ingreso'));
  }

  deleteIngreso(insumo: any, i: number): void {
    Swal.fire({
      title: '¿Estás seguro de que quieres borrar este registro?',
      showCancelButton: true,
      confirmButtonText: 'Sí',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        console.log(insumo);
        this.aybService
          .mutationDeleteIngreso(insumo.id)
          .pipe(takeUntil(this._unsubscribeOnDestroy$))
          .subscribe((data: any) => {
            Swal.fire({
              position: 'top-end',
              icon: 'success',
              title: 'El registro se eliminó satisfactoriamente.',
              showConfirmButton: false,
              timer: 4000,
            });
            console.log(insumo.id);
            const index = this.dataSource.data.findIndex(
              (item: any) => item.id === insumo.id,
            );
            if (index > -1) {
              console.log(index);
              this.dataSource.data.splice(index, 1);
              this.dataSource._updateChangeSubscription();
            }
          });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        console.log('false');
      }
    });
  }

  navigateToAddIngreso() {
    this.router
      .navigate([`/gestion/ingresos/nuevo`])
      .then(() => console.log('Navigated to Add Ingreso'));
  }
}
